import { Button, Col, Divider, Row, Select } from "antd";
import { useContext, useEffect, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

import styles from "./style.module.scss";

import AppContext from "../../../context/app/context";
import HomePageSectionsArrangementContext from "../../../context/arrange-sections-page-home/context";
import { execute } from "../../../utils/helpers/execute";
import FieldBuilder from "../../form-components/field-builder";
import Spin from "../../general/antd/spin";
import HomeHomePageSectionsArrangementContextProvider from "../../../context/arrange-sections-page-home/provider";
import { getTranslations } from "../../../utils/helpers/translations";
import { languages } from "../../../utils/helpers/constants";
import { getSectionStatus, getShape } from "../../../models/arrange_sections_page_home/enum";
import { IBanner } from "../../../models/banner/response";
import EndPoints from "../../../services/end-points";
import FormItem from "../../general/form-item";
import { IHomePageList } from "../../../models/home-page-list/response";
import { getSectionType, SectionType } from "../../../context/arrange-sections-page-home/enum";
const Option = Select.Option;
const Form = () => {
  const { details, actions, loading } = useContext(HomePageSectionsArrangementContext);
  const { direction } = useContext(AppContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const { initialValues } = location.state || {};
  const [banners, setBanners] = useState<IBanner[]>([]);
  const [bannersLoading, setBannersLoading] = useState<boolean>(false);
  const [productLists, setProductLists] = useState<IHomePageList[]>([]);
  const [productListsLoading, setProductListsLoading] = useState<boolean>(false);
  const [tagKeyword, setTagKeyword] = useState<string>();

  const methods = useForm<any>({
    defaultValues: {
      title: getTranslations(details?.translations?.title),
      shape: details?.shape || undefined,
      priority: details?.priority || undefined,
      sectionType: details?.sectionType || undefined,
      productListId: details?.productListId || undefined,
      bannersIds: details?.banners?.map((banner) => banner?.id),
      status: details?.status || false,
    },
  });

  const { handleSubmit, reset, control } = methods;

  const shape = useWatch({
    control,
    name: "shape",
  });

  const sectionType = useWatch({
    control,
    name: "sectionType",
  });

  const showBannersField = sectionType === SectionType.BANNER;
  const showProductListField = sectionType === SectionType.PRODUCT_LIST;

  const [loadingSubmit, setLoadingSubmit] = useState(false);

  useEffect(() => {
    const getBanners = async () => {
      setBannersLoading(true);
      try {
        const { data } = await EndPoints.banner.getAllBanners({
          filters: [
            {
              name: "shape",
              value: shape,
              operation: "eq",
            },
          ],
          perPage: -1,
        });
        setBanners(data.data);
      } catch (error) {
        console.error("Error fetching banners:", error);
      } finally {
        setBannersLoading(false);
      }
    };

    if (shape) {
      getBanners();
    } else {
      setBanners([]);
    }
  }, [direction, shape]);

  useEffect(() => {
    const getProductLists = async () => {
      setProductListsLoading(true);
      try {
        const { data } = await EndPoints.homePageList.getAllHomePageLists({
          perPage: -1,
          light: true
        });
        setProductLists(data.data);
      } catch (error) {
        console.error("Error fetching ProductLists:", error);
      } finally {
        setProductListsLoading(false);
      }
    };
    getProductLists();
  }, [direction]);

  const onSubmit = async (data: any) => {
    await execute({
      callback: async () => {
        setLoadingSubmit(true);
        if (initialValues?.id) {
          await actions.updateHomePageSectionsArrangement(initialValues.id, data);
        } else
          actions.createHomePageSectionsArrangement(data)
        navigate(-1);
      },
      fallback: (error) => {
        console.error("Failed to update:", error);
      },
      finallyCallback: () => {
        setLoadingSubmit(false);
      },
      throwException: false,
    });
  };

  useEffect(() => {
    if (initialValues) {
      reset({
        title: getTranslations(initialValues?.translations?.title),
        shape: initialValues?.shape ?? undefined,
        priority: initialValues?.priority ?? undefined,
        sectionType: initialValues?.sectionType ?? undefined,
        productListId: initialValues?.homepageListing?.id ?? undefined,
        bannersIds: initialValues?.banners?.map((banner) => banner?.id) ?? [],
        status: details?.status || false,
      });
    }
  }, [initialValues, reset]);

  const getMaxBannersCount = (shapeValue: number) => {
    switch (shapeValue) {
      case 2: return 1;
      case 3: return 2;
      case 4: return 3;
      default: return undefined;
    }
  };

  useEffect(() => {
    if (shape) {
      const maxCount = getMaxBannersCount(shape);
      const currentBanners = methods.getValues('bannersIds') || [];

      if (maxCount && currentBanners.length > maxCount) {
        methods.setValue('bannersIds', currentBanners.slice(0, maxCount));
      }
    }
  }, [shape]);

  return (
    <FormProvider {...methods}>
      <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        <div>
          <span className={styles.title}>
            {initialValues?.id
              ? t("update_home_page_section")
              : t("add_home_page_section")
            }
          </span>
          <Divider className={styles.divider} />

          {loading.includes("update") ? (
            <div style={{ height: "60vh" }}>
              <Spin />
            </div>
          ) : (
            <div className={styles.children}>
              <Row gutter={[16, 12]}>
                {
                  languages?.map((lang, index) => (
                    <Col span={12} key={index}>
                      <FieldBuilder
                        name={`title[${lang?.code}]`}
                        label={`${t("title")} (${lang?.name})`}
                        input={{ type: "text" }}
                        rules={{ required: true }}
                      />
                    </Col>
                  ))}
                <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                  <FieldBuilder
                    name="sectionType"
                    label={t("section_type")}
                    width="large"
                    input={{
                      type: "select",
                      options: [2, 3]?.map((pl, index) => {
                        return {
                          key: index,
                          label: t(getSectionType(pl)),
                          value: pl,
                        };
                      }),
                    }}
                    rules={{ required: true }}
                  />
                </Col>
                {showBannersField && <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                  <FormItem label={t("shape")} required={showBannersField}>
                    <Controller
                      name="shape"
                      control={control}
                      rules={{ required: showBannersField }}
                      render={({ field: { ...field } }) => {
                        return (
                          <Select
                            {...field}
                            style={{ width: "100%" }}
                            placeholder={t("rateable_type")}
                          >
                            {[1, 2, 3, 4, 5].map((inc, index) => {
                              return (
                                <Option key={index} value={inc}>
                                  {t(getShape(inc))}
                                </Option>
                              );
                            })}
                          </Select>
                        );
                      }}
                    />
                  </FormItem>
                </Col>}
                {showBannersField && <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                  <FormItem label={t("banners")} required={showBannersField}>
                    <Controller
                      name="bannersIds"
                      control={control}
                      rules={{ required: showBannersField }}
                      render={({ field: { ...field } }) => {
                        const maxCount = getMaxBannersCount(shape);
                        return (
                          <Select
                            {...field}
                            loading={bannersLoading}
                            onSearch={(val) => {
                              setTagKeyword(val);
                            }}
                            style={{ width: "100%" }}
                            allowClear
                            mode="multiple"
                            placeholder={maxCount
                              ? t("select_up_to_n_banners", { n: maxCount })
                              : t("banners")
                            }
                            filterOption={false}
                            maxCount={maxCount}
                            onSelect={(value, option) => {
                              const currentValues = field.value || [];
                              if (maxCount && currentValues.length >= maxCount) {
                                field.onChange([...currentValues.slice(1), value]);
                              } else {
                                field.onChange([...currentValues, value]);
                              }
                            }}
                            disabled={!shape}
                          >
                            {banners
                              .filter((tr) =>
                                tagKeyword
                                  ? tr?.text?.includes(tagKeyword)
                                  : true
                              )
                              .map((tr, index) => {
                                return (
                                  <Option key={index} value={tr?.id}>
                                    {tr?.text}
                                  </Option>
                                );
                              })}
                          </Select>
                        );
                      }}
                    />
                  </FormItem>
                </Col>}
                <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                  <FieldBuilder
                    name="priority"
                    label={t("priority")}
                    input={{
                      type: "number",
                    }}
                    rules={{ required: true }}
                  />
                </Col>

                {showProductListField && <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                  <FieldBuilder
                    name="productListId"
                    label={t("product_list")}
                    width="large"
                    input={{
                      type: "select",
                      loading: productListsLoading,
                      allowSearch: true,
                      options: productLists?.map((pl, index) => {
                        return {
                          key: index,
                          label: pl.title,
                          value: pl.id,
                        };
                      }),
                    }}
                    rules={{ required: showProductListField }}
                  />
                </Col>}
                <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                  <FieldBuilder
                    name="status"
                    label={`${t("status")}`}
                    width="large"
                    rules={{ required: true }}
                    input={{
                      type: "select",
                      loading: false,
                      options: [1, 2].map((state, index) => {
                        return {
                          key: index,
                          label: t(getSectionStatus(state)),
                          value: state,
                        };
                      }),
                    }}
                  />
                </Col>
              </Row>
            </div>
          )}
        </div>

        <div>
          <Divider className={styles.divider} />
          <div className={styles.formFooter}>
            <Button onClick={() => navigate(-1)}>{t("cancel")}</Button>
            <Button loading={loadingSubmit} htmlType="submit" type="primary">
              {t("save")}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

const HomePageSectionsArrangementForm = () => {
  return (
    <HomeHomePageSectionsArrangementContextProvider>
      <Form />
    </HomeHomePageSectionsArrangementContextProvider>
  );
};

export default HomePageSectionsArrangementForm;